import { useTranslation } from "react-i18next";

import {
  ComplexListItem,
  InfoDrawerMeta,
  InfoDrawerSection,
  SideDrawer,
  Switch,
  useSnackbarContext,
  writeToClipboard,
} from "@inductiveautomation/ws-components";
import { usePublicProfileActions } from "./PublicProfile.actions";
import { TranslateKey } from "../../../i18n/i18n.types";
import { useState } from "react";
import { GetProfileApiResponse } from "../api/profile.api";
import { camelCase } from "lodash";
import { shareKey } from "./PublicProfile.types";

const profileFields = [
  "share_primary_email_address",
  "share_phone_number",
  "share_job_title",
  "share_country",
] as const;
const trainingFields = [
  "share_certifications",
  "share_credentials",
  "share_elective_studies",
] as const;

const sections = [
  {
    key: "profile" as const,
    fields: profileFields,
  },
  {
    key: "training" as const,
    fields: trainingFields,
  },
];

const PublicProfileDrawer = ({
  enabled,
  setEnabled,
  url,
  open,
  onClose,
  publicProfile,
  handleFieldUpdate,
}: {
  enabled: boolean;
  setEnabled: (enabled: boolean) => Promise<{ data?: object; error?: object }>;
  url: string;
  open: boolean;
  onClose: () => void;
  publicProfile: GetProfileApiResponse["publicProfile"];
  handleFieldUpdate: ({
    key,
    value,
  }: {
    key: shareKey;
    value: boolean;
  }) => Promise<{
    data?: object;
    error?: object;
  }>;
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "profile.public_profile",
  });
  const { t: unprefixedT } = useTranslation();
  const actions = usePublicProfileActions(enabled, setEnabled, url);
  const { addSnackbar } = useSnackbarContext();
  const {
    sharePrimaryEmailAddress,
    sharePhoneNumber,
    shareJobTitle,
    shareCountry,
    shareCertifications,
    shareCredentials,
    shareElectiveStudies,
  } = publicProfile;
  // TODO(API)
  const [enabledFields, setEnabledFields] = useState({
    sharePrimaryEmailAddress,
    sharePhoneNumber,
    shareJobTitle,
    shareCountry,
    shareCertifications,
    shareCredentials,
    shareElectiveStudies,
  });
  // END-TODO(API)

  // const fieldKey = (
  //   key: (typeof sections)[number]["key"],
  //   field: (typeof sections)[number]["fields"],
  //   suffix: "heading" | "subtext",
  // ): TranslateKey => {
  //   return `drawer.sections.${key}.${field as typeof profileFields}.${suffix}` as TranslateKey;
  // };
  //

  const toggleField = (field: string) => {
    const key: shareKey = camelCase(field) as shareKey;
    const clonedEnabledFields = { ...enabledFields };
    const updatedValue = !clonedEnabledFields[key];
    clonedEnabledFields[key] = updatedValue;
    setEnabledFields(clonedEnabledFields);
    handleFieldUpdate({ key, value: updatedValue })
      .then(({ error }) => {
        if (error) {
          addSnackbar(t("drawer.switch_failure"), {
            id: "public-profile-updated-error",
            type: "error",
          });
        } else {
          addSnackbar(t("drawer.switch_success_message"), {
            id: "public-profile-updated",
          });
        }
      })
      .catch(() =>
        addSnackbar(t("drawer.switch_success_message"), {
          id: "public-profile-updated",
        })
      );
  };

  return (
    <SideDrawer
      testId="public-profile-drawer"
      open={open}
      variant="modal"
      hideBackdrop
      onClose={onClose}
      contentSpacing="fullWidth"
      headerProps={{
        icon: "person-public",
        avatarProps: {
          iconAvatarColor: "primary",
        },
        overline: t("drawer.overline"),
        heading: t("drawer.heading"),
        showCloseButton: true,
      }}
      footerProps={{
        actions,
        actionsLabel: unprefixedT("common.actions"),
        chip: {
          testId: "profile-public-profile-chip-container",
          variant: "status",
          //state: ChipState.NEUTRAL,
          id: "profile-public-profile-chip",
          label: enabled
            ? t("card.statuses.enabled.label")
            : t("card.statuses.disabled.label"),
        },
      }}
    >
      <InfoDrawerMeta
        heading={t("drawer.sections.details.heading")}
        disabled={!enabled}
        fields={[
          {
            label: t("drawer.sections.details.profile_url.label"),
            value: url,
            onCopy: () => {
              writeToClipboard(url)
                .then(() =>
                  addSnackbar(
                    t(
                      "drawer.sections.details.profile_url.copy_link_success_message"
                    )
                  )
                )
                .catch((error: unknown) => {
                  addSnackbar(
                    t(
                      "drawer.sections.details.profile_url.copy_link_error_message"
                    ),
                    { type: "error" }
                  );
                  console.error(error);
                });
            },
          },
        ]}
      />

      {sections.map(({ key, fields }) => (
        <InfoDrawerSection
          key={key}
          heading={t(`drawer.sections.${key}.heading`)}
          fullWidth
          hasDivider={false}
          gap="0"
        >
          <div style={{ marginTop: "0.5rem" }} />
          {fields.map((field) => (
            <ComplexListItem
              key={`public-profile-${field}`}
              id={`public-profile-${field}`}
              testId={`public-profile-${field}`}
              heading={unprefixedT(
                `profile.public_profile.drawer.sections.${key}.${field}.heading` as TranslateKey
              )}
              subtext={unprefixedT(
                `profile.public_profile.drawer.sections.${key}.${field}.subtext` as TranslateKey
              )}
              disabled={!enabled}
              density="drawer"
              onSelection={() => toggleField(field)}
              keepInfoOpaqueWhenDisabled
              info={
                <Switch
                  id={`public-profile-${field}-switch`}
                  testId={`public-profile-${field}-switch`}
                  checked={enabledFields[camelCase(field) as shareKey]}
                  disabled={!enabled}
                  onChange={() => toggleField(field)}
                  label=""
                />
              }
            />
          ))}
        </InfoDrawerSection>
      ))}
    </SideDrawer>
  );
};

export default PublicProfileDrawer;
